// Slideshow overlay templates exposed by the Video Micro-Service V2. The service
// registers template1–9; this list wires the ones surfaced in the UI. `id` is the
// value sent to the API; `preview` is a looping MP4 render used on the card. From
// the video2 code: template1=Scroll, template2=SlideIn, template3=ScrollDualTone
// (all 1280x720 landscape), template4=ScrollPortrait (720x1280 portrait, for
// Reels/Stories), template5=Modern Editorial (1280x720), template6=Split Magazine
// (1280x720), template7=Spotlight Badge (1280x720), template8=Vertical Editorial
// (720x1280 portrait), template9=Spotlight Badge portrait (720x1280, for Reels/Stories).
//
// Previews are MP4 clips served from the public/ dir (public/slideshow-templates/),
// so they stay out of the JS bundle. NOTE: labels/descriptions are placeholders
// pending an output-video review. template1–11 each have a preview clip; templates 1–4
// are hidden from the picker (see `hidden`).

// Public-dir preview URL (CRA serves public/ at PUBLIC_URL).
const previewUrl = (name: string): string =>
    `${process.env.PUBLIC_URL}/slideshow-templates/${name}.mp4`;

export type SlideshowTemplateId =
    | "template1"
    | "template2"
    | "template3"
    | "template4"
    // Modern Editorial (Template5, 1280x720): branded title intro, captioned
    // photo slides, and a "Let's connect" closing.
    | "template5"
    // Split Magazine (Template6, 1280x720): photo on the left, a solid brand-color
    // panel on the right holding the caption / title — "listing flyer" style.
    | "template6"
    // Spotlight Badge (Template7, 1280x720): full-bleed photos, a circular headshot
    // + a "Just Listed" pill badge on the intro, captioned slides, centred closing.
    | "template7"
    // Vertical Editorial (Template8, 720x1280 portrait): the portrait sibling of
    // template5 — full-bleed photos, a dynamic logo chip, headline + headshot on
    // the intro/outro only, and a captioned tour.
    | "template8"
    // Spotlight Badge portrait (Template9, 720x1280): the vertical sibling of
    // template7 for Reels/Stories.
    | "template9"
    // Spotlight Badge with a Ken Burns zoom (Template10, 1280x720): template7 in
    // every respect except that each photo eases open from a 10% punch-in to its
    // full framing. The opening and closing slides hold still.
    | "template10"
    // Spotlight Badge portrait with a Ken Burns zoom (Template11, 720x1280): the
    // vertical sibling of template10.
    | "template11"
    // "video1" is the classic (Video 1) image-to-video flow — no V2 text/color/
    // font overlays; the backend routes it to the image-to-video endpoints.
    | "video1";

export interface SlideshowTemplate {
    id: SlideshowTemplateId;
    label: string;
    description: string;
    /** Looping MP4 render used as the card preview. Omitted = blank card preview. */
    preview?: string;
    /** When true, the template is filtered out of the selection gallery. */
    hidden?: boolean;
}

export const SLIDESHOW_TEMPLATES: ReadonlyArray<SlideshowTemplate> = [
    {
        // Scroll preset (1280x720): two-tone banner slides into a lower corner.
        id: "template1",
        label: "Banner",
        description: "Two-line banner in a lower corner",
        preview: previewUrl("template1"),
        hidden: true,
    },
    {
        // SlideIn preset (1280x720): large text directly over each photo.
        id: "template2",
        label: "Bold",
        description: "Large headline over each photo",
        preview: previewUrl("template2"),
        hidden: true,
    },
    {
        // ScrollDualTone preset (1280x720): minimal single-line dual-tone bar.
        id: "template3",
        label: "Minimal",
        description: "Single clean line in a lower corner",
        preview: previewUrl("template3"),
        hidden: true,
    },
    {
        // ScrollPortrait preset (720x1280): vertical, lower banner.
        id: "template4",
        label: "Vertical",
        description: "Portrait (9:16) for Reels & Stories",
        preview: previewUrl("template4"),
        hidden: true,
    },
    {
        // ModernIntro/ModernCaption/ModernOutro preset (1280x720): editorial title
        // card, captioned slides, branded closing.
        id: "template5",
        label: "Editorial",
        description: "Magazine-style title, captioned photos & a branded closing",
        preview: previewUrl("template5"),
    },
    {
        // Magazine preset (1280x720): photo on the left, brand-color side panel on
        // the right holds the caption / title.
        id: "template6",
        label: "Flyer",
        description: "Photo beside a brand-color caption panel",
        preview: previewUrl("template6"),
    },
    {
        // Spotlight preset (1280x720): circular headshot + a "Just Listed" pill
        // badge intro, captioned slides, centred closing.
        id: "template7",
        label: "Spotlight",
        description: "Your headshot, front & center",
        preview: previewUrl("template7"),
    },
    {
        // ModernPortrait preset (720x1280): portrait sibling of template5 — photo-led
        // with a dark lower-third, dynamic logo and a captioned tour.
        id: "template8",
        label: "Editorial Reel",
        description: "Vertical Editorial for Reels & Stories",
        preview: previewUrl("template8"),
    },
    {
        // SpotlightPortrait preset (720x1280): portrait Spotlight for Reels /
        // Stories.
        id: "template9",
        label: "Spotlight Reel",
        description: "Vertical Spotlight for Reels & Stories",
        preview: previewUrl("template9"),
    },
    {
        // Ken Burns Spotlight (1280x720): template7's furniture, but the photo
        // tour moves.
        id: "template10",
        label: "Spotlight Motion",
        description: "Spotlight with a slow zoom on each photo",
        preview: previewUrl("template10"),
    },
    {
        // Ken Burns Spotlight portrait (720x1280).
        id: "template11",
        label: "Spotlight Motion Reel",
        description: "Vertical Spotlight with a slow zoom",
        preview: previewUrl("template11"),
    },
    {
        id: "video1",
        label: "Classic",
        description: "Plain photo slideshow with an optional address overlay",
        preview: previewUrl("video1"),
    },
];

// The first non-hidden template — used as the default selection in the picker.
export const DEFAULT_SLIDESHOW_TEMPLATE: SlideshowTemplateId =
    SLIDESHOW_TEMPLATES.find((t) => !t.hidden)?.id ?? SLIDESHOW_TEMPLATES[0].id;

// Templates that use the legacy Video 1 image-to-video flow (no text/color/font
// overlays). Branding collapses to an optional logo + optional music.
export const isVideo1Template = (id: SlideshowTemplateId): boolean =>
    id === "video1";

// Portrait (9:16) templates render best with vertical photos; landscape photos
// (most listing photos) get cropped or letterboxed, so we warn on selection.
export const isPortraitTemplate = (id: SlideshowTemplateId): boolean =>
    id === "template4" ||
    id === "template8" ||
    id === "template9" ||
    id === "template11";

// Ken Burns templates render every tour slide as its own video clip, so a long
// photo set pushes the render past the video service's timeout. This blocks the
// wizard before it submits; the api enforces the same ceiling on the request and
// on its own auto-rotation selection.
//
// Hand-mirrored from marabot-api src/constants/slideshow-template.constant.ts
// (KEN_BURNS_MAX_IMAGES / isKenBurnsTemplate). Nothing detects drift between the
// two, so this number and that one move together or the wizard blocks at a limit
// the api does not share.
export const KEN_BURNS_MAX_IMAGES = 6;

export const isKenBurnsTemplate = (id: SlideshowTemplateId): boolean =>
    id === "template10" || id === "template11";
